import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

// eslint-disable-next-line no-restricted-imports
import {
  FilterChangedEvent,
  ProcessCellForExportParams,
} from '@ag-grid-community/core'
import { NetworkStatus, useLazyQuery } from '@apollo/client'
import { useSelector } from 'react-redux'

import { JoorPayOrdersDocument } from '__generated__/atlas-types'

import { formatDate } from 'utils/dates'

import { atlasClient } from 'graphql/client'
import { useRenderToasters } from 'hooks/useRenderToasters'
import { EMPTY_CELL_CONTENT } from 'shop/products/utils'
import { getDateFormat } from 'store/currentUser/selectors'

import { AgGridReactType, SelectionChangedEvent } from 'components/AgGrid/types'

import { formatOrderEvent } from './joorPayOrders.configuration'
import { usePayOrdersQuery } from './joorPayOrders.queries'
import { TableColumns, TableRowData } from './joorPayOrders.types'
import {
  getDateRangeFromNow,
  mapOrderEdgesToTableRows,
} from './joorPayOrders.utils'

const MONTHS_BEFORE = 3

const use90DaysOrders = (skip: boolean) => {
  const [filteredOrders, setFilteredOrders] = useState<TableRowData[]>([])
  const [filteredOrdersCount, setFilteredOrdersCount] = useState<number>(0)
  const [filteredOrdersError, setFilteredOrdersError] = useState<boolean>(false)

  const createdDateRange = useMemo(() => getDateRangeFromNow(MONTHS_BEFORE), [])

  const [getOrders, { error: apolloError, loading: filteredOrdersLoading }] =
    useLazyQuery(JoorPayOrdersDocument, {
      client: atlasClient,
      fetchPolicy: 'network-only',
      variables: {
        filters: {
          hasPayments: true,
          createdDateRange,
        },
      },
    })

  useEffect(() => {
    if (!skip) {
      if (filteredOrders.length === 0) {
        getOrders()
          .then((resp) => {
            const orders = mapOrderEdgesToTableRows(resp.data)
            const count = resp.data?.orders?.totalCount ?? 0
            setFilteredOrders(orders)
            setFilteredOrdersCount(count)
          })
          .catch(() => {
            setFilteredOrdersError(true)
          })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip])

  return {
    filteredOrders,
    filteredOrdersCount,
    filteredOrdersError: Boolean(apolloError) || filteredOrdersError,
    filteredOrdersLoading,
  }
}

const useAllOrders = (skip: boolean) => {
  const [hasErrors, setHasErrors] = useState(false)

  const { data, loading, error, fetchMore } = usePayOrdersQuery({
    filters: {
      hasPayments: true,
    },
    skip,
  })

  const loadMoreOrders = useCallback(
    async (offset: number, limit: number = 100) => {
      try {
        const { error, errors, networkStatus } = await fetchMore({
          variables: {
            filters: { hasPayments: true },
            offset,
            limit,
          },
          updateQuery: (previousOrders, { fetchMoreResult }) => {
            const newOrderEdges = fetchMoreResult.orders.edges ?? []
            if (!newOrderEdges.length) {
              return previousOrders
            }
            const result = {
              orders: {
                ...fetchMoreResult.orders,
                edges: [
                  ...previousOrders.orders.edges,
                  ...fetchMoreResult.orders.edges,
                ],
              },
            }
            return result
          },
        })

        if (error || errors || networkStatus === NetworkStatus.error) {
          throw new Error()
        }
      } catch (error) {
        setHasErrors(true)
      }
    },
    [fetchMore],
  )

  const allOrders: TableRowData[] = useMemo(
    () => mapOrderEdgesToTableRows(data),
    [data],
  )

  const allOrdersCount = data?.orders?.totalCount ?? 0

  useEffect(() => {
    if (allOrders.length < allOrdersCount) {
      loadMoreOrders(allOrders.length)
    }
  }, [allOrdersCount, allOrders.length, loadMoreOrders])

  return {
    allOrders,
    allOrdersCount,
    allOrdersLoading: loading,
    allOrdersError: Boolean(error) || hasErrors,
  }
}

export const usePayOrdersQueries = (
  isJoorPayEnabled: boolean,
  requestAll: boolean,
) => {
  const {
    filteredOrders,
    filteredOrdersCount,
    filteredOrdersError,
    filteredOrdersLoading,
  } = use90DaysOrders(!isJoorPayEnabled || requestAll)

  const { allOrders, allOrdersCount, allOrdersLoading, allOrdersError } =
    useAllOrders(!isJoorPayEnabled || !requestAll)

  return {
    orders: requestAll ? allOrders : filteredOrders,
    total: requestAll ? allOrdersCount : filteredOrdersCount,
    isDataLoading: allOrdersLoading || filteredOrdersLoading,
    requestError: requestAll ? allOrdersError : filteredOrdersError,
  }
}

export const useSelectionState = () => {
  const [checkedRows, setCheckedRows] = useState<TableRowData[]>([])
  const [singleActionRow, setSingleActionRow] = useState<TableRowData[]>([])

  const handleCheckboxSelectionChanged = useCallback(
    (event: SelectionChangedEvent): void => {
      setCheckedRows(event.api.getSelectedNodes().map((row) => row?.data))
    },
    [],
  )

  const handleSetSingleActionRow = useCallback((row: TableRowData) => {
    setSingleActionRow([row])
  }, [])

  return {
    checkedRows,
    singleActionRow,
    handleCheckboxSelectionChanged,
    handleSetSingleActionRow,
  }
}

export const useModalStates = () => {
  const [isSendInvoiceModalOpen, setIsSendInvoiceModalOpen] = useState(false)
  const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] = useState(false)
  const [isSendPaymentReqModalOpen, setIsSendPaymentReqModalOpen] =
    useState(false)

  const handleSendInvoiceModal = useCallback((openStatus: boolean) => {
    setIsSendInvoiceModalOpen(openStatus)
  }, [])

  const handleChangeStatusModal = useCallback((openStatus: boolean) => {
    setIsChangeStatusModalOpen(openStatus)
  }, [])

  const handleSendPaymentReqModal = useCallback((openStatus: boolean) => {
    setIsSendPaymentReqModalOpen(openStatus)
  }, [])

  return {
    isSendInvoiceModalOpen,
    isChangeStatusModalOpen,
    isSendPaymentReqModalOpen,
    handleSendInvoiceModal,
    handleChangeStatusModal,
    handleSendPaymentReqModal,
  }
}

const processExportCells =
  (dateFormat: string) =>
  (params: ProcessCellForExportParams): string => {
    let value = params.value

    if (
      [
        TableColumns.FirstRequest as string,
        TableColumns.LastTransaction as string,
        TableColumns.LastNotified as string,
        TableColumns.DueDate as string,
        TableColumns.OrderCreatedDate as string,
        TableColumns.ShipComplete as string,
        TableColumns.ShipStart as string,
      ].includes(params.column.getId())
    ) {
      // invalid dates when null or undefined
      // treated to show as empty cell in the csv '-'
      if (params.value) {
        value = formatDate(params.value, dateFormat.toUpperCase())
      } else {
        value = EMPTY_CELL_CONTENT
      }
    }

    if (params.column.getId() === (TableColumns.Reminders as string)) {
      value = value.message
    }

    if (params.column.getId() === (TableColumns.Linesheet as string)) {
      value = (value as { name: string }).name
    }

    if (params.column.getId() === (TableColumns.Event as string)) {
      value = formatOrderEvent({ value })
    }

    return value
  }

export const usePayOrdersFilters = () => {
  const dateFormat = useSelector(getDateFormat)
  const gridRef = useRef<AgGridReactType>(null)
  const [searchText, setSearchText] = useState('')
  const [isBeingFiltered, setIsBeingFiltered] = useState(false)

  useEffect(() => {
    gridRef.current?.api?.setQuickFilter(searchText)
  }, [searchText])

  const handleSearch = useCallback((searchText: string) => {
    setSearchText(searchText)
  }, [])

  const handleExport = useCallback(
    (areRowsSelected?: boolean) => {
      const columnKeys = gridRef?.current?.columnApi
        ?.getAllGridColumns()
        .filter(
          (col) =>
            col.getId() !== TableColumns.Actions &&
            col.getId() !== TableColumns.Selection,
        )
        .map((col) => col.getId())

      gridRef?.current?.api?.exportDataAsCsv({
        columnKeys,
        onlySelected: areRowsSelected,
        processCellCallback: processExportCells(dateFormat),
      })
    },
    [dateFormat],
  )

  const onFiltersChange = (event: FilterChangedEvent) => {
    const filterModel = event.api.getFilterModel()

    const colFiltersHaveValue =
      Object.keys(filterModel).length > 0 || Boolean(searchText)

    setIsBeingFiltered(colFiltersHaveValue)

    // setTimeout() in order to make it work > Issue found here:
    // https://github.com/ag-grid/ag-grid/issues/3849#issuecomment-1651728392
    setTimeout(() => {
      if (gridRef?.current?.api?.getDisplayedRowCount() === 0) {
        gridRef?.current?.api?.showNoRowsOverlay()
      }
    }, 100)
  }

  const handleResetFilters = useCallback(() => {
    gridRef.current?.api?.setFilterModel(null)
    setSearchText('')
    setIsBeingFiltered(false)
  }, [])

  return {
    gridRef,
    isBeingFiltered,
    searchText,
    handleSearch,
    handleExport,
    onFiltersChange,
    handleResetFilters,
  }
}

export const useStatusUpdateToasters = () => {
  const { renderSuccessToast, renderErrorToast, renderWarningToast } =
    useRenderToasters()

  const renderStatusUpdateToast = (
    successfulCount: number,
    erroredCount: number,
  ) => {
    if (erroredCount === 0) {
      renderSuccessToast({
        title: 'Success changing status',
        description: `${successfulCount} order(s) have been updated`,
      })
    } else if (successfulCount === 0) {
      renderErrorToast({
        title: 'Error changing status',
        description: `Failed to change status for ${erroredCount} order(s)`,
      })
    } else {
      const totalCount = successfulCount + erroredCount
      renderWarningToast({
        title: 'Partial status change',
        description: `${erroredCount} of ${totalCount} order(s) failed to
          update.`,
      })
    }
  }

  return { renderStatusUpdateToast }
}
